// Shared helpers for normalising auth-related errors coming from
// @its-engineering-and-development/jwt-auth-client. The client collapses every
// non-MFA failure into a plain Error and drops the HTTP status, so we match on
// known transport messages to separate network failures from auth failures.

const NETWORK_ERROR_MESSAGES = new Set([
  "Network error - unable to reach auth service",
  "Request timeout",
]);

const UNCONFIRMED_USER_ERROR_CODES = new Set(["USER_NOT_CONFIRMED"]);

const UNCONFIRMED_USER_MESSAGES = new Set([
  "user is not confirmed",
  "user account is not confirmed",
  "this account hasn't been verified yet",
  "user_not_confirmed",
]);

const RECOVERABLE_LOGIN_MESSAGES = new Set([
  "if an account exists, a code has been sent",
]);

export const NETWORK_ERROR_USER_MESSAGE =
  "Can't reach the auth service. Check your connection and try again.";

function normalizeAuthMessage(message: string): string {
  return message
    .trim()
    .replace(/[.!]+$/, "")
    .toLowerCase();
}

function readErrorCode(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("code" in error)) {
    return null;
  }

  const { code } = error as { code?: unknown };
  return typeof code === "string" ? code : null;
}

export function isNetworkError(error: unknown): boolean {
  // jwt-auth-client@2.5.2 wraps fetch failures in a TypeError with a
  // network-flavoured message. Narrow on both the type AND the message so an
  // unrelated TypeError (e.g. a programming bug) is not misreported as a
  // network outage to the user.
  if (
    error instanceof TypeError &&
    /fetch|network|failed to fetch/i.test(error.message)
  ) {
    return true;
  }
  return error instanceof Error && NETWORK_ERROR_MESSAGES.has(error.message);
}

export function isUnconfirmedUserAuthError(error: unknown): boolean {
  const errorCode = readErrorCode(error);
  if (errorCode && UNCONFIRMED_USER_ERROR_CODES.has(errorCode)) {
    return true;
  }

  return (
    error instanceof Error &&
    UNCONFIRMED_USER_MESSAGES.has(normalizeAuthMessage(error.message))
  );
}

export function isRecoverableLoginMessage(message: string): boolean {
  return RECOVERABLE_LOGIN_MESSAGES.has(normalizeAuthMessage(message));
}

export function mapAuthError(error: unknown, fallback: string): string {
  if (isNetworkError(error)) {
    return NETWORK_ERROR_USER_MESSAGE;
  }
  return fallback;
}
